import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  MenuItem,
  Select,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  Badge,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Grid,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useAuth } from "../../../context/AuthContext";
import axios from "../../../config/axiosConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import clienteServicioService from "../../historialCliente/services/clienteServicioService";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// FASE 1 de la Devolución (tipo_ot="Devolucion"): se lista el equipo Activo y los
// accesorios con cantidad que el cliente YA tiene, y se elige qué se devuelve —
// igual de directo que AsignarEquiposParOT/AsignarEquiposVariosOT (Adición) para
// ARMAR la selección. A diferencia de Adición, acá el equipo NO se clasifica
// todavía: siempre queda en "Espera" (igual que guarda.php del sistema viejo). La
// clasificación final (Inactivo/Mantenimiento/Baja/Robado) es la FASE 2, un paso
// aparte que ve otro usuario cuando la OT llega a "Asignado" — ver
// ClasificarDevolucionOT.jsx. Los accesorios sí se devuelven directo a stock acá
// (Mantenimiento/Baja), sin fase intermedia, igual que en el sistema viejo. AVL/Scada
// no entra acá: usa su propio flujo de devolución
// (modules/requerimiento/reqDevolucionModel.js del backend).
//
// El equipo activo del cliente se pagina igual que el inventario de
// AsignarEquiposParOT/AsignarEquiposVariosOT (clientes grandes como YPFB llegaban a
// tener cientos de equipos activos, y traerlos todos de una hacía la página pesada).
// La selección (checkbox) guarda los datos de cada fila en el momento del click, no una
// referencia a la lista visible — así sobrevive a cambiar de página, de tipo o de
// búsqueda sin perderse.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };
const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
};
// Input nativo (Box component="input") en vez de TextField variant="standard" para
// las celdas de tabla sin label — mismo criterio que AsignarEquiposVariosOT, pero
// con solo un borde inferior para imitar el look "standard" original.
const inputCompactoSx = {
  fontFamily: "inherit",
  fontSize: 10.5,
  padding: "2px 2px",
  border: "none",
  borderBottom: "1px solid",
  borderColor: "divider",
  outline: "none",
  bgcolor: "transparent",
  color: "text.primary",
  "&:focus": { borderColor: "primary.main" },
  "&:disabled": { color: "text.disabled" },
};

const DESTINOS_ACCESORIO = [
  { value: "mantenimiento", label: "Mantenimiento" },
  { value: "baja", label: "Baja" },
];

const TIPOS_TKR = ["Radio", "Bateria"];
const TIPOS_OTROS = ["Varios", "Chip"];
const TIPO_LABEL = { Radio: "Radios", Bateria: "Baterías", Varios: "Equipos", Chip: "Chips" };
const PAGE_SIZE = 15;

const endpointDeTipo = (tipo) => {
  if (tipo === "Radio") return "/api/equipos-radio";
  if (tipo === "Bateria") return "/api/equipos-bateria";
  if (tipo === "Chip") return "/api/equipos-chip";
  return "/api/equipos-varios";
};

// Misma tarjeta con ícono + borde de acento que ya usa AsignarEquiposParOT.jsx/
// AsignarEquiposVariosOT.jsx (Adición) — layout de 2 columnas pedido explícitamente
// para que Devolución se vea igual.
const SectionCard = ({ title, icon, color = "primary", extra, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 1,
      borderTop: "3px solid",
      borderTopColor: `${color}.main`,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: (theme) => alpha(theme.palette[color].main, theme.palette.mode === "dark" ? 0.28 : 0.12),
          color: `${color}.main`,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
      {extra}
    </Stack>
    {children}
  </Paper>
);

const DevolucionEquiposOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, esTKR, tipoServicio, servicioDestino, onEquiposActualizados, onDevueltosCambiados }) => {
  const { user } = useAuth();
  const tipos = esTKR ? TIPOS_TKR : TIPOS_OTROS;
  const [tipo, setTipo] = useState(tipos[0]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loadingEquipos, setLoadingEquipos] = useState(true);
  const [error, setError] = useState("");

  const [accesorios, setAccesorios] = useState([]);

  // Equipos: { [key]: { tipoEquipo, id, identificador, observacion } } — guarda todo lo
  // necesario para devolver el equipo aunque ya no esté en la página/tipo visible.
  const [seleccionEquipos, setSeleccionEquipos] = useState({});
  // Accesorios: { [modelo|ciudad]: { cantidad, destino, observacion } }
  const [seleccionAccesorios, setSeleccionAccesorios] = useState({});

  const [guardando, setGuardando] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errores, setErrores] = useState([]);
  const [equipoDialog, setEquipoDialog] = useState(null);

  // La decisión "¿es transferencia a otro cliente?" ya se tomó al emitir la OT (ver
  // FormularioOT.jsx) y quedó fija en ot_solicitud.servicio_destino — acá solo se
  // resuelve el nombre del cliente destino para mostrarlo, ya no se elige.
  const [clienteDestino, setClienteDestino] = useState(null);
  const [resultadoTransferencia, setResultadoTransferencia] = useState(null);
  const [confirmTransferenciaOpen, setConfirmTransferenciaOpen] = useState(false);

  useEffect(() => {
    if (!servicioDestino) {
      setClienteDestino(null);
      return;
    }
    axios
      .get(`/api/cliente-servicio/${servicioDestino}`)
      .then((res) => setClienteDestino(res.data))
      .catch((err) => console.error("Error al cargar el cliente destino de la transferencia", err));
  }, [servicioDestino]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargarPagina = (nextPage = page, buscarTexto = buscarAplicado) => {
    setLoadingEquipos(true);
    setError("");
    axios
      .get(endpointDeTipo(tipo), {
        params: {
          estado: "Activo",
          cod_servicio: cod_servicio_cliente,
          // Solo equipo de la sucursal del usuario logueado — no tiene sentido
          // devolver/transferir equipo que está físicamente en otra ciudad. user
          // puede venir null en el primer render tras un refresh (mientras
          // useAuth() todavía está rehidratando la sesión).
          ciudad: user?.sucursal || undefined,
          buscar: buscarTexto,
          page: nextPage,
          pageSize: PAGE_SIZE,
        },
      })
      .then((res) => {
        if (tipo === "Chip") {
          const { rows = [], total: totalChips = 0 } = res.data || {};
          setItems(rows.map((r) => ({ key: `chip-${r.ID}`, id: r.ID, tipoEquipo: "Chip", identificador: r.numero, modelo: r.modelo, ciudad: r.ciudad })));
          setTotal(Number(totalChips));
          setPage(nextPage);
        } else {
          const data = res.data || {};
          // placa/grupo/id_ref solo existen en Radio y Varios (Bateria/Chip no los
          // tienen) — igual se copian siempre, quedan undefined sin problema.
          setItems((data.items || []).map((r) => ({
            key: `${tipo.toLowerCase()}-${r.ID}`, id: r.ID, tipoEquipo: tipo, identificador: r.serie, modelo: r.modelo, ciudad: r.ciudad,
            placa: r.placa, grupo: r.grupo, id_ref: r.id_ref,
          })));
          setTotal(Number(data.total || 0));
          setPage(Number(data.page || nextPage));
        }
      })
      .catch((err) => {
        console.error("Error al cargar equipos activos del cliente", err);
        setError("No se pudo cargar el equipo activo del cliente.");
      })
      .finally(() => setLoadingEquipos(false));
  };

  useEffect(() => {
    setBuscar("");
    setBuscarAplicado("");
    cargarPagina(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, cod_servicio_cliente]);

  // Cantidad de equipo Activo por CADA tipo (no solo el que está seleccionado
  // en el ToggleButtonGroup) — se muestra como badge sobre "Radios"/"Baterías"
  // para no tener que entrar a cada pestaña solo para ver cuántos hay. Se pide
  // con pageSize:1 (solo interesa el "total" de la respuesta, no las filas).
  const [conteos, setConteos] = useState({});

  const cargarConteos = () => {
    Promise.all(
      tipos.map((t) =>
        axios
          .get(endpointDeTipo(t), {
            params: {
              estado: "Activo",
              cod_servicio: cod_servicio_cliente,
              ciudad: user?.sucursal || undefined,
              page: 1,
              pageSize: 1,
            },
          })
          .then((res) => [t, Number(res.data?.total) || 0])
          .catch(() => [t, null])
      )
    ).then((pares) => setConteos(Object.fromEntries(pares)));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargarConteos, [cod_servicio_cliente, esTKR, user?.sucursal]);

  // Si cambia el servicio (TKR <-> otro), vuelve al primer tipo de esa lista.
  useEffect(() => {
    setTipo((esTKR ? TIPOS_TKR : TIPOS_OTROS)[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esTKR]);

  const cargarAccesorios = () => {
    clienteServicioService.getAccesoriosActualesCliente(cod_servicio_cliente)
      .then((accesoriosData) => setAccesorios(Array.isArray(accesoriosData?.items) ? accesoriosData.items : []))
      .catch((err) => console.error("Error al cargar accesorios del cliente", err));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargarAccesorios, [cod_servicio_cliente]);

  const handleBuscar = () => {
    setBuscarAplicado(buscar.trim());
    cargarPagina(1, buscar.trim());
  };

  // Lo que ya se guardó como Devolucion en ESTA OT — se muestra en una sección propia
  // (igual que "Equipos en la Orden" en Adición) para que quede claro que la
  // devolución sí se guardó, y de paso alimenta el conteo que usa OrdenTrabajoPaso
  // para bloquear "Finalizar Devolución de Equipos" mientras no haya nada guardado
  // todavía — evita que se confunda con "Guardar Devolución" de acá.
  const [equiposDevueltos, setEquiposDevueltos] = useState([]);
  const [accesoriosDevueltos, setAccesoriosDevueltos] = useState([]);

  const cargarDevueltos = () => {
    const peticiones = esTKR
      ? [axios.get("/api/historial-radio", { params: { cod_ot } }), axios.get("/api/historial-bateria", { params: { cod_ot } })]
      : [axios.get("/api/historial-varios", { params: { cod_ot } }), axios.get("/api/historial-chip", { params: { cod_ot } })];

    Promise.all([...peticiones, axios.get("/api/historial-accesorio", { params: { cod_ot } })])
      .then(([res1, res2, resAcc]) => {
        const equipos = esTKR
          ? [
              ...(res1.data || []).map((r) => ({ ...r, key: `radio-${r.id_historial}`, tipoEquipo: "Radio", tipoDialog: "radio", identificador: r.serie })),
              ...(res2.data || []).map((r) => ({ ...r, key: `bateria-${r.id_historial}`, tipoEquipo: "Bateria", tipoDialog: "bateria", identificador: r.serie })),
            ]
          : [
              ...(res1.data || []).map((r) => ({ ...r, key: `varios-${r.id_historial}`, tipoEquipo: r.tipo || "Varios", tipoDialog: "varios", identificador: r.serie })),
              ...(res2.data || []).map((r) => ({ ...r, key: `chip-${r.id_historial}`, tipoEquipo: "Chip", tipoDialog: "chip", identificador: r.numero })),
            ];
        const equiposFiltrados = equipos.filter((r) => r.operacion === "Devolucion");
        const accesoriosDev = (resAcc.data || []).filter((r) => r.operacion === "Devolucion");
        setEquiposDevueltos(equiposFiltrados);
        setAccesoriosDevueltos(accesoriosDev);
        if (onDevueltosCambiados) onDevueltosCambiados(equiposFiltrados.length + accesoriosDev.length);
      })
      .catch((err) => console.error("Error al cargar equipos/accesorios devueltos", err));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargarDevueltos, [cod_ot, esTKR]);

  // La selección no se limpia al cambiar de página/tipo/búsqueda (igual que en
  // AsignarEquiposParOT) — guarda los datos de la fila en el momento del click.
  const toggleEquipo = (row) => {
    setSeleccionEquipos((prev) => {
      if (prev[row.key]) {
        const { [row.key]: _quitado, ...resto } = prev;
        return resto;
      }
      return {
        ...prev,
        [row.key]: {
          tipoEquipo: row.tipoEquipo, id: row.id, identificador: row.identificador, observacion: "",
          // Prellenados con el valor actual del equipo — editables en el dialog de
          // confirmación de la transferencia (solo aplica a Radio/Varios).
          placa: row.placa || "", grupo: row.grupo || "", id_ref: row.id_ref || "",
        },
      };
    });
  };

  const setCampoEquipo = (key, campo, valor) => {
    setSeleccionEquipos((prev) => ({ ...prev, [key]: { ...prev[key], [campo]: valor } }));
  };

  const claveAccesorio = (a) => `${a.modelo}|${a.ciudad}`;

  const setCampoAccesorio = (a, campo, valor) => {
    const key = claveAccesorio(a);
    setSeleccionAccesorios((prev) => ({
      ...prev,
      [key]: { cantidad: "", destino: "mantenimiento", observacion: "", ...prev[key], [campo]: valor },
    }));
  };

  const equiposSeleccionados = Object.entries(seleccionEquipos).map(([key, datos]) => ({ key, ...datos }));
  const accesoriosSeleccionados = accesorios
    .map((a) => ({ accesorio: a, datos: seleccionAccesorios[claveAccesorio(a)] }))
    .filter(({ datos }) => Number(datos?.cantidad) > 0);

  const hayCambios = equiposSeleccionados.length > 0 || accesoriosSeleccionados.length > 0;

  const devolverEquipo = async (row) => {
    // Siempre "Espera": la clasificación final (Inactivo/Mantenimiento/Baja/Robado) es
    // la Fase 2, ver ClasificarDevolucionOT.jsx.
    const estadoFinal = "Espera";
    const observacion = row.observacion || "";

    if (row.tipoEquipo === "Radio") {
      await axios.put(`/api/equipos-radio/${row.id}`, {
        estado: estadoFinal, cod_servicio: 0, asignado: "", grupo: "", placa: "", id_ref: "",
      });
      await axios.post(`/api/historial-radio/`, {
        serie: row.identificador, cod_ot, propiedad: "TRS", asignado: cod_cliente, ciudad: user.sucursal,
        estado: estadoFinal, operacion: "Devolucion", observacion, usu: user.username,
      });
    } else if (row.tipoEquipo === "Bateria") {
      await axios.put(`/api/equipos-bateria/${row.id}`, {
        estado: estadoFinal, cod_servicio: 0, asignado: "", relacion: "",
      });
      await axios.post(`/api/historial-bateria/`, {
        serie: row.identificador, cod_ot, propiedad: "TRS", asignado: cod_cliente, ciudad: user.sucursal,
        estado: estadoFinal, operacion: "Devolucion", observacion, usu: user.username,
      });
    } else if (row.tipoEquipo === "Chip") {
      await axios.put(`/api/equipos-chip/${row.id}`, {
        estado: estadoFinal, cod_servicio: 0, asignado: "", relacion: "", precio: "0", ciudad: user.sucursal,
      });
      await axios.post(`/api/historial-chip/`, {
        numero: row.identificador, cod_ot, asignado: cod_cliente, ciudad: user.sucursal,
        estado: estadoFinal, operacion: "Devolucion", observacion, usu: user.username,
      });
    } else {
      await axios.put(`/api/equipos-varios/${row.id}`, {
        estado: estadoFinal, cod_servicio: 0, asignado: "",
      });
      await axios.post(`/api/historial-varios/`, {
        serie: row.identificador, cod_ot, propiedad: "TRS", asignado: cod_cliente, ciudad: user.sucursal,
        estado: estadoFinal, operacion: "Devolucion", observacion, usu: user.username,
      });
    }
  };

  // Modo transferencia: una sola llamada con toda la selección + destino — el backend
  // ya NO incluye automáticamente la pareja (Bateria de un Radio, o Chip/Varios
  // pareados): solo viaja lo que el usuario seleccionó, y del lado que se queda
  // (o del propio item, si es Bateria/Chip) se rompe la `relacion` en vez de
  // preservarla — ver romperRelacion() en otTransferenciaClienteModel.js. placa/grupo
  // sí se preservan, a diferencia de devolverEquipo() que los limpia.
  const guardarTransferencia = async () => {
    if (!clienteDestino || !equiposSeleccionados.length) return;
    setGuardando(true);
    setErrores([]);
    try {
      // placa/grupo/id_ref: solo tienen sentido para Radio/Varios, pero se mandan
      // igual para los demás (undefined) — el backend los ignora si el tipo no aplica.
      const items = equiposSeleccionados.map(({ tipoEquipo, id, identificador, placa, grupo, id_ref }) => ({
        tipoEquipo, id, identificador, placa, grupo, id_ref,
      }));
      const { data } = await axios.post(`/api/ot-solicitudes/${cod_ot}/confirmar-transferencia-cliente`, {
        items,
        cod_servicio_destino: clienteDestino.cod_servicio_cliente,
      });
      setResultadoTransferencia(data);
      // OJO: no se llama a onEquiposActualizados() acá — esa función (ver
      // OrdenTrabajoPaso.jsx's handleEquiposActualizados) fuerza la OT a estado 2 si
      // en memoria seguía en "Emitido"(1), lo que pisaría el estado 7 (Finalizado) que
      // el backend ya dejó en confirmarTransferenciaClienteOTTx. El panel de resultado
      // de acá reemplaza toda la UI, así que no hace falta refrescar la lista del padre.
    } catch (err) {
      setErrores([err?.response?.data?.error || "No se pudo confirmar la transferencia."]);
    } finally {
      setGuardando(false);
    }
  };

  const guardar = async () => {
    if (servicioDestino) return guardarTransferencia();
    if (!hayCambios) return;
    setGuardando(true);
    setErrores([]);
    const fallidos = [];
    const equiposExitosos = [];
    const accesoriosExitosos = [];

    for (const row of equiposSeleccionados) {
      try {
        await devolverEquipo(row);
        equiposExitosos.push(row.key);
      } catch (err) {
        fallidos.push(err?.response?.data?.error || `Error al devolver "${row.identificador}"`);
      }
    }

    for (const { accesorio, datos } of accesoriosSeleccionados) {
      try {
        await axios.post("/api/equipos-accesorio/devolver", {
          modelo: accesorio.modelo,
          ciudad: accesorio.ciudad,
          cantidad: Number(datos.cantidad),
          destino: datos.destino || "mantenimiento",
          cod_ot,
          observacion: datos.observacion || "",
        });
        accesoriosExitosos.push(claveAccesorio(accesorio));
      } catch (err) {
        fallidos.push(err?.response?.data?.error || `Error al devolver accesorio "${accesorio.modelo}"`);
      }
    }

    // Solo se limpia (y se refresca la lista) lo que sí se guardó — lo que falló se
    // deja tal cual estaba escrito, para que el error quede junto a la fila que lo
    // causó en vez de parecer que la página se reseteó sin haber guardado nada.
    if (equiposExitosos.length) {
      setSeleccionEquipos((prev) => {
        const next = { ...prev };
        equiposExitosos.forEach((key) => delete next[key]);
        return next;
      });
    }
    if (accesoriosExitosos.length) {
      setSeleccionAccesorios((prev) => {
        const next = { ...prev };
        accesoriosExitosos.forEach((key) => delete next[key]);
        return next;
      });
    }

    setErrores(fallidos);
    if (equiposExitosos.length || accesoriosExitosos.length) {
      setOpenSnackbar(true);
      cargarPagina();
      cargarAccesorios();
      cargarDevueltos();
      cargarConteos();
      if (onEquiposActualizados) onEquiposActualizados();
    }
    setGuardando(false);
  };

  if (resultadoTransferencia) {
    return (
      <Paper variant="outlined" sx={{ p: 3, mt: 1, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          Transferencia confirmada
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Se finalizó esta devolución y se creó la OT de Adición #{resultadoTransferencia.cod_adicion} para{" "}
          {clienteDestino?.nombre_cliente || `servicio #${servicioDestino}`}, ya con{" "}
          {resultadoTransferencia.items?.length || 0} equipo(s) cargado(s).
        </Typography>
        <Button
          variant="contained"
          component={RouterLink}
          to={`/orden-de-trabajo/ot/${resultadoTransferencia.cod_adicion}`}
        >
          Ver OT de Adición #{resultadoTransferencia.cod_adicion}
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}

      <Grid container spacing={1.5}>
        {/* ── Columna izquierda: inventario disponible del cliente ── */}
        <Grid size={{ xs: 12, md: 8 }}>
          <SectionCard title="Equipos Activos del Cliente" icon={<Inventory2Icon fontSize="small" />} color="primary">
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                color="primary"
                value={tipo}
                onChange={(e, nuevo) => nuevo && setTipo(nuevo)}
                sx={{ height: 30 }}
              >
                {tipos.map((t) => (
                  <ToggleButton key={t} value={t} sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                    <Badge
                      badgeContent={conteos[t] ?? 0}
                      color="error"
                      max={999}
                      showZero
                      sx={{ "& .MuiBadge-badge": { right: -8, top: 2 } }}
                    >
                      {TIPO_LABEL[t]}
                    </Badge>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1, rowGap: 1 }}>
              <TextField
                size="small"
                label="Buscar"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleBuscar();
                }}
                sx={{ minWidth: 160, ...controlSx }}
              />
              <IconButton size="small" onClick={handleBuscar} title="Buscar">
                <SearchIcon fontSize="small" />
              </IconButton>
              <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
              <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${equiposSeleccionados.length}`} sx={{ height: 20, fontSize: 11 }} />
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Página {total ? page : 0} / {total ? totalPages : 0}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                disabled={loadingEquipos || page <= 1}
                onClick={() => cargarPagina(page - 1)}
                sx={{ minHeight: 26, height: 26, fontSize: 11 }}
              >
                Anterior
              </Button>
              <Button
                size="small"
                variant="outlined"
                endIcon={<NavigateNextIcon />}
                disabled={loadingEquipos || page >= totalPages}
                onClick={() => cargarPagina(page + 1)}
                sx={{ minHeight: 26, height: 26, fontSize: 11 }}
              >
                Siguiente
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={
                  guardando ||
                  (servicioDestino ? !clienteDestino || !equiposSeleccionados.length : !hayCambios)
                }
                onClick={() => (servicioDestino ? setConfirmTransferenciaOpen(true) : guardar())}
                sx={{ minHeight: 26, height: 26, fontSize: 11, px: 2 }}
              >
                {guardando ? (
                  <CircularProgress size={16} color="inherit" />
                ) : servicioDestino ? (
                  "Confirmar Transferencia"
                ) : (
                  "Guardar Devolución"
                )}
              </Button>
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, borderRadius: 0.5 }}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
                    <TableCell sx={headCellSx}>Identificador</TableCell>
                    <TableCell sx={headCellSx}>Modelo</TableCell>
                    <TableCell sx={headCellSx}>Ciudad</TableCell>
                    <TableCell sx={headCellSx}>Observación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingEquipos ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                        <CircularProgress size={18} />
                      </TableCell>
                    </TableRow>
                  ) : !items.length ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          Este cliente no tiene {TIPO_LABEL[tipo].toLowerCase()} activos.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => {
                      const checked = Boolean(seleccionEquipos[row.key]);
                      return (
                        <TableRow key={row.key} hover selected={checked}>
                          <TableCell>
                            <Checkbox size="small" checked={checked} onChange={() => toggleEquipo(row)} sx={{ p: 0 }} />
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              onClick={() => setEquipoDialog({ tipo: row.tipoEquipo.toLowerCase(), identificador: row.identificador })}
                              sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                            >
                              {row.identificador}
                            </Box>
                          </TableCell>
                          <TableCell>{row.modelo}</TableCell>
                          <TableCell>{row.ciudad}</TableCell>
                          <TableCell>
                            <Box
                              component="input"
                              disabled={!checked}
                              value={seleccionEquipos[row.key]?.observacion || ""}
                              onChange={(e) => setCampoEquipo(row.key, "observacion", e.target.value)}
                              sx={{ ...inputCompactoSx, width: "100%" }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          {!servicioDestino && (
            <Box sx={{ mt: 1.5 }}>
              <SectionCard title="Accesorios del Cliente" icon={<Inventory2Icon fontSize="small" />} color="secondary">
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280, borderRadius: 0.5 }}>
                  <Table size="small" stickyHeader sx={tableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Modelo</TableCell>
                        <TableCell sx={headCellSx}>Ciudad</TableCell>
                        <TableCell sx={headCellSx}>Cantidad Actual</TableCell>
                        <TableCell sx={{ ...headCellSx, width: 100 }}>A Devolver</TableCell>
                        <TableCell sx={{ ...headCellSx, width: 140 }}>Destino</TableCell>
                        <TableCell sx={headCellSx}>Observación</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!accesorios.length ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                              Este cliente no tiene accesorios con cantidad registrada.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        accesorios.map((a) => {
                          const key = claveAccesorio(a);
                          const datos = seleccionAccesorios[key];
                          return (
                            <TableRow key={key} hover>
                              <TableCell>{a.modelo}</TableCell>
                              <TableCell>{a.ciudad}</TableCell>
                              <TableCell>{a.cantidad}</TableCell>
                              <TableCell>
                                <Box
                                  component="input"
                                  type="number"
                                  min={0}
                                  max={a.cantidad}
                                  value={datos?.cantidad ?? ""}
                                  onChange={(e) => setCampoAccesorio(a, "cantidad", e.target.value)}
                                  sx={{ ...inputCompactoSx, width: 70 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Select
                                  variant="standard"
                                  size="small"
                                  value={datos?.destino || "mantenimiento"}
                                  onChange={(e) => setCampoAccesorio(a, "destino", e.target.value)}
                                  sx={{ fontSize: 11, width: "100%" }}
                                >
                                  {DESTINOS_ACCESORIO.map((op) => (
                                    <MenuItem key={op.value} value={op.value} sx={{ fontSize: 11 }}>{op.label}</MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Box
                                  component="input"
                                  value={datos?.observacion || ""}
                                  onChange={(e) => setCampoAccesorio(a, "observacion", e.target.value)}
                                  sx={{ ...inputCompactoSx, width: "100%" }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
            </Box>
          )}

          {!!errores.length && (
            <MuiAlert severity="error" variant="outlined" sx={{ mt: 1.5, fontSize: 11, py: 0 }}>
              {errores.map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
            </MuiAlert>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={
                guardando ||
                (servicioDestino ? !clienteDestino || !equiposSeleccionados.length : !hayCambios)
              }
              onClick={() => (servicioDestino ? setConfirmTransferenciaOpen(true) : guardar())}
              sx={{ minHeight: 28, height: 28, fontSize: 11.5, px: 2 }}
            >
              {guardando ? (
                <CircularProgress size={16} color="inherit" />
              ) : servicioDestino ? (
                "Confirmar Transferencia"
              ) : (
                "Guardar Devolución"
              )}
            </Button>
          </Box>
        </Grid>

        {/* ── Columna derecha: transferencia (fija) o lo ya devuelto en esta OT ── */}
        <Grid size={{ xs: 12, md: 4 }}>
          {servicioDestino ? (
            <SectionCard title="Transferencia a otro Cliente" icon={<SwapHorizIcon fontSize="small" />} color="info">
              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>
                Destino: {clienteDestino?.nombre_cliente || `servicio #${servicioDestino}`}
                {clienteDestino?.descripcion ? ` (${clienteDestino.descripcion})` : ""}
              </Typography>
              <Alert severity="info" variant="outlined" sx={{ fontSize: 11, py: 0 }}>
                Solo el equipo seleccionado se re-vincula directo al cliente destino,
                preservando placa/grupo. Si tenía una Batería o Chip relacionado que no se
                seleccionó, esa relación se rompe (queda con el cliente origen, ya
                desvinculada). Esta OT se finaliza sin pasar por clasificación, y se crea
                automáticamente una OT de Adición ya finalizada para el destino.
              </Alert>
            </SectionCard>
          ) : (
            <SectionCard
              title="Devuelto en esta OT"
              icon={<AssignmentTurnedInIcon fontSize="small" />}
              color="success"
              extra={
                <Chip
                  size="small"
                  color="success"
                  label={equiposDevueltos.length + accesoriosDevueltos.length}
                  sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                />
              }
            >
              {!equiposDevueltos.length && !accesoriosDevueltos.length ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  Todavía no se guardó ninguna devolución en esta OT.
                </Typography>
              ) : (
                <>
                  {!!equiposDevueltos.length && (
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1, maxHeight: 220, borderRadius: 0.5 }}>
                      <Table size="small" stickyHeader sx={tableSx}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={headCellSx}>Identificador</TableCell>
                            <TableCell sx={headCellSx}>Tipo</TableCell>
                            <TableCell sx={headCellSx}>Estado Final</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {equiposDevueltos.map((r) => (
                            <TableRow key={r.key} hover>
                              <TableCell>
                                <Box
                                  component="span"
                                  onClick={() => setEquipoDialog({ tipo: r.tipoDialog, identificador: r.identificador })}
                                  sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                                >
                                  {r.identificador}
                                </Box>
                              </TableCell>
                              <TableCell>{r.tipoEquipo}</TableCell>
                              <TableCell>{r.estado}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {!!accesoriosDevueltos.length && (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 180, borderRadius: 0.5 }}>
                      <Table size="small" stickyHeader sx={tableSx}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={headCellSx}>Modelo</TableCell>
                            <TableCell sx={headCellSx}>Cantidad</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {accesoriosDevueltos.map((a) => (
                            <TableRow key={a.id} hover>
                              <TableCell>{a.modelo}</TableCell>
                              <TableCell>{a.cantidad}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </SectionCard>
          )}
        </Grid>
      </Grid>

      <Dialog open={confirmTransferenciaOpen} onClose={() => !guardando && setConfirmTransferenciaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 800 }}>Confirmar transferencia</DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Se van a transferir estos equipos a{" "}
            <strong>{clienteDestino?.nombre_cliente || `servicio #${servicioDestino}`}</strong>
            {clienteDestino?.descripcion ? ` (${clienteDestino.descripcion})` : ""}:
          </Typography>
          <List dense disablePadding sx={{ mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            {equiposSeleccionados.map((row) => {
              const tieneDatosVehiculo = row.tipoEquipo === "Radio" || row.tipoEquipo === "Varios";
              return (
                <ListItem key={row.key} divider sx={{ display: "block", py: 1 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: tieneDatosVehiculo ? 0.75 : 0 }}>
                    {row.tipoEquipo}: {row.identificador}
                  </Typography>
                  {row.observacion && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      {row.observacion}
                    </Typography>
                  )}
                  {tieneDatosVehiculo && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <TextField
                        size="small"
                        label="Placa"
                        value={row.placa || ""}
                        onChange={(e) => setCampoEquipo(row.key, "placa", e.target.value)}
                        sx={{ width: 130, "& .MuiInputBase-input": { fontSize: 11.5 } }}
                      />
                      <TextField
                        size="small"
                        label="Grupo"
                        value={row.grupo || ""}
                        onChange={(e) => setCampoEquipo(row.key, "grupo", e.target.value)}
                        sx={{ width: 130, "& .MuiInputBase-input": { fontSize: 11.5 } }}
                      />
                      <TextField
                        size="small"
                        label="ID Ref"
                        value={row.id_ref || ""}
                        onChange={(e) => setCampoEquipo(row.key, "id_ref", e.target.value)}
                        sx={{ width: 130, "& .MuiInputBase-input": { fontSize: 11.5 } }}
                      />
                    </Stack>
                  )}
                </ListItem>
              );
            })}
          </List>
          <Alert severity="info" variant="outlined" sx={{ fontSize: 11.5, py: 0 }}>
            Si alguno tenía una Batería o Chip relacionado que no está en esta lista, esa
            relación se rompe al confirmar — no viaja junto, se queda desvinculada con el
            cliente origen.
          </Alert>
          {!!errores.length && (
            <MuiAlert severity="error" variant="outlined" sx={{ mt: 1.5, fontSize: 11, py: 0 }}>
              {errores.map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
            </MuiAlert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTransferenciaOpen(false)} disabled={guardando}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            disabled={guardando}
            onClick={guardarTransferencia}
            startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Confirmar Transferencia
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Devolución registrada correctamente.
        </MuiAlert>
      </Snackbar>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default DevolucionEquiposOT;
